import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { UserAssignment } from "../../../lib/types/adminProjects";

interface AssignmentActionModalProps {
  type: string;
  assignment: UserAssignment;
  onClose: () => void;
  onReview: (params: {
    userProjectId: string;
    payload: { action: "approve" | "reject"; rejectionReason?: string };
  }) => Promise<any>;
  onUpdateStatus: (params: {
    userProjectId: string;
    payload: { status: "active" | "completed" | "terminated"; reason?: string };
  }) => Promise<any>;
  onUnassign: (params: {
    userProjectId: string;
    reason?: string;
  }) => Promise<any>;
  isReviewing: boolean;
  isUpdatingStatus: boolean;
  isUnassigning: boolean;
}

const AssignmentActionModal: React.FC<AssignmentActionModalProps> = ({
  type,
  assignment,
  onClose,
  onReview,
  onUpdateStatus,
  onUnassign,
  isReviewing,
  isUpdatingStatus,
  isUnassigning,
}) => {
  const [reason, setReason] = useState("");
  const isPending = isReviewing || isUpdatingStatus || isUnassigning;

  const handleAction = async () => {
    try {
      switch (type) {
        case "approve":
          await onReview({
            userProjectId: assignment._id,
            payload: { action: "approve" },
          });
          break;
        case "reject":
          await onReview({
            userProjectId: assignment._id,
            payload: { action: "reject", rejectionReason: reason },
          });
          break;
        case "activate":
          await onUpdateStatus({
            userProjectId: assignment._id,
            payload: { status: "active" },
          });
          break;
        case "complete":
          await onUpdateStatus({
            userProjectId: assignment._id,
            payload: { status: "completed" },
          });
          break;
        case "terminate":
          await onUpdateStatus({
            userProjectId: assignment._id,
            payload: { status: "terminated", reason },
          });
          break;
        case "unassign":
          await onUnassign({ userProjectId: assignment._id, reason });
          break;
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const config: Record<
    string,
    {
      title: string;
      description: string;
      buttonText: string;
      buttonClass: string;
      needsReason: boolean;
    }
  > = {
    view: {
      title: "Assignment Details",
      description: "",
      buttonText: "Close",
      buttonClass: "bg-gray-100 text-gray-700",
      needsReason: false,
    },
    approve: {
      title: "Approve Application",
      description:
        "Are you sure you want to approve this application? The user will be able to start working on this project.",
      buttonText: "Approve",
      buttonClass: "bg-emerald-600 text-white hover:bg-emerald-700",
      needsReason: false,
    },
    reject: {
      title: "Reject Application",
      description: "Please provide a reason for rejecting this application.",
      buttonText: "Reject",
      buttonClass: "bg-red-600 text-white hover:bg-red-700",
      needsReason: true,
    },
    activate: {
      title: "Mark as Active",
      description:
        "This will mark the user as actively working on this project.",
      buttonText: "Mark Active",
      buttonClass: "bg-blue-600 text-white hover:bg-blue-700",
      needsReason: false,
    },
    complete: {
      title: "Mark as Completed",
      description: "This will mark the project as completed for this user.",
      buttonText: "Mark Completed",
      buttonClass: "bg-purple-600 text-white hover:bg-purple-700",
      needsReason: false,
    },
    terminate: {
      title: "Terminate Assignment",
      description: "Please provide a reason for terminating this assignment.",
      buttonText: "Terminate",
      buttonClass: "bg-red-600 text-white hover:bg-red-700",
      needsReason: true,
    },
    unassign: {
      title: "Unassign Project",
      description:
        "Are you sure you want to unassign this project from the user?",
      buttonText: "Unassign",
      buttonClass: "bg-red-600 text-white hover:bg-red-700",
      needsReason: false,
    },
  };

  const currentConfig = config[type] || config.view;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {currentConfig.title}
          </h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {type === "view" ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Project
                </label>
                <p className="text-gray-900">{assignment.projectId.title}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Verification Link
                </label>
                <p className="text-[#8B52FF] break-all">
                  {assignment.verificationLink}
                </p>
              </div>
              {assignment.applicationAnswers && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Availability
                  </label>
                  <p className="text-gray-900">
                    {assignment.applicationAnswers.availability} hrs/week
                  </p>
                </div>
              )}
              {assignment.assignmentNote && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Assignment Note
                  </label>
                  <p className="text-gray-900">{assignment.assignmentNote}</p>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-gray-600">{currentConfig.description}</p>

              {currentConfig.needsReason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason{" "}
                    {type === "reject" || type === "terminate" ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      "(Optional)"
                    )}
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#8B52FF]/20 focus:border-[#8B52FF] resize-none"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-6 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 border border-gray-200 rounded-md"
          >
            {type === "view" ? "Close" : "Cancel"}
          </button>
          {type !== "view" && (
            <button
              onClick={handleAction}
              disabled={isPending || (currentConfig.needsReason && !reason)}
              className={`px-6 py-2 font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 ${currentConfig.buttonClass}`}
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {currentConfig.buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentActionModal;
